import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { getData, setData } from '../services/storage';
import { Button, Modal } from '../components/Forms';

const sections = [
  { id: 'hero', label: 'Hero Section', icon: '🚀' },
  { id: 'about', label: 'About Section', icon: '👥' },
  { id: 'services', label: 'Services Section', icon: '⚡' },
  { id: 'process', label: 'Process Section', icon: '🔄' },
  { id: 'industries', label: 'Industries Section', icon: '🏢' },
  { id: 'portfolio', label: 'Portfolio Section', icon: '📁' },
  { id: 'testimonials', label: 'Testimonials', icon: '⭐' },
  { id: 'footer', label: 'Footer Content', icon: '📄' },
];

const WebsiteContent = () => {
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [formData, setFormData] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const data = getData('content') || {};
    setContents(data);
    setLoading(false);
  }, []);

  const openEditor = (section) => {
    setSelectedSection(section);
    const existing = contents[section.id];
    setFormData(existing ? JSON.stringify(existing, null, 2) : '{\n  \n}');
  };

  const handleSave = () => {
    if (!selectedSection) return;
    setSaving(true);
    setSuccess('');
    try {
      const parsed = JSON.parse(formData);
      const updated = { ...contents, [selectedSection.id]: parsed };
      setContents(updated);
      setData('content', updated);
      setSuccess(`${selectedSection.label} updated successfully!`);
      setTimeout(() => setSuccess(''), 3000);
      setSelectedSection(null);
    } catch (err) {
      alert('Invalid JSON. Please check your syntax.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;

  return (
    <div>
      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm mb-6">{success}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => openEditor(section)}
            className="text-left bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md hover:border-purple-300 transition-all group cursor-pointer"
          >
            <div className="text-2xl mb-3">{section.icon}</div>
            <h3 className="text-sm font-semibold text-slate-700 group-hover:text-purple-600 transition-colors">{section.label}</h3>
            <p className="text-xs text-slate-400 mt-1">{contents[section.id] ? 'Content exists' : 'No content yet'}</p>
          </button>
        ))}
      </div>

      <Modal isOpen={!!selectedSection} onClose={() => setSelectedSection(null)} title={`Edit: ${selectedSection?.label}`} size="xl">
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Edit the JSON content below. Changes will be reflected on the website.</p>
          <textarea
            value={formData}
            onChange={(e) => setFormData(e.target.value)}
            rows={20}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm font-mono resize-none"
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button variant="secondary" onClick={() => setSelectedSection(null)} type="button">Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              <Save size={18} />
              Save Content
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WebsiteContent;
